import type { VersionedJson } from '@builder4/shared/migrations/types';

const BODY_SIZES = ['small', 'medium', 'large'] as const;
const SHARED_BODY_FIELDS = [
  'fontColor',
  'letterSpacing',
  'fontWeight',
  'lineHeight',
  'backgroundColor',
] as const;

interface StyleNode {
  [key: string]: unknown;
}

interface DataWithStyles extends VersionedJson {
  typography?: {
    body?: StyleNode;
    [key: string]: unknown;
  };
}

/**
 * Moves shared body typography properties (fontColor, letterSpacing, fontWeight,
 * lineHeight, backgroundColor) from the top level of body into each size variant
 * (small, medium, large) so that each body text size can have independent styling.
 */
export default function toV4<T extends VersionedJson>(data: T): T {
  const typedData = data as DataWithStyles;
  const { typography } = typedData;

  if (!typography || typeof typography !== 'object') {
    return data;
  }

  const body = typography.body;
  if (!body || typeof body !== 'object') {
    return data;
  }

  // Extract the shared properties from the top level
  const sharedProps: Record<string, unknown> = {};
  for (const field of SHARED_BODY_FIELDS) {
    if (field in body) {
      sharedProps[field] = body[field];
    }
  }

  // Build new body object with only fontFamily at top level and
  // shared properties merged into each size variant
  const newBody: StyleNode = { fontFamily: body.fontFamily };
  for (const size of BODY_SIZES) {
    const existing = (body[size] as StyleNode) || {};
    newBody[size] = { ...sharedProps, ...existing };
  }

  return {
    ...data,
    typography: {
      ...typography,
      body: newBody,
    },
  } as T;
}
